import React, { useState, useEffect } from 'react';
import { ShieldAlert, AlertTriangle, CheckCircle, RefreshCw, Terminal, HelpCircle } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const FraudControl = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [confirmModal, setConfirmModal] = useState({ visible: false, id: null });
  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/fraud/alerts`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setAlerts(response.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!confirmModal.id) return;
    try {
      const res = await axios.put(`${constant.API_BASE_URL}/admin/business/fraud/alerts/${confirmModal.id}/resolve`, {}, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        showToast("Alert marked as resolved!");
        setConfirmModal({ visible: false, id: null });
        fetchAlerts();
      }
    } catch (err) {
      showToast("Failed to resolve alert", 'error');
      setConfirmModal({ visible: false, id: null });
    }
  };

  const filteredAlerts = alerts.filter(a => {
    if (!searchQuery) return true;
    const s = searchQuery.toLowerCase();
    return (
      (a.first_name || '').toLowerCase().includes(s) ||
      (a.last_name || '').toLowerCase().includes(s) ||
      (a.email || '').toLowerCase().includes(s) ||
      (a.ip_address || '').toLowerCase().includes(s) ||
      (a.alert_type || '').toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Fraud & Security Engine</h2>
          <p className="text-slate-500 mt-1">Automatic detection of duplicate recharge patterns, suspicious device logging, and warning counts audits.</p>
        </div>
        <button 
          onClick={fetchAlerts}
          className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition active:scale-95 shadow-sm"
        >
          Re-Scan System
        </button>
      </div>

      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-50 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ShieldAlert size={18} className="text-pink-600 animate-pulse" /> Active Fraud Warnings
            </h3>
            <span className="text-xs font-bold text-slate-400 uppercase">Live Security Monitoring</span>
          </div>
          <input 
            type="text" 
            placeholder="Search IP, User or Type..." 
            className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition min-w-[250px]"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Scanning ledger & device tokens...</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className="p-20 text-center text-slate-400">
            <CheckCircle size={48} className="mx-auto mb-4 text-green-400 opacity-80" />
            <p>System secure! No active warnings or duplicate alerts found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-4">Alert Category</th>
                  <th className="p-4">Description</th>
                  <th className="p-4">Associated User</th>
                  <th className="p-4">IP / Device Context</th>
                  <th className="p-4">Severity</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredAlerts.map((alert) => (
                  <tr key={alert.id} className="hover:bg-slate-50/50 transition">
                    <td className="p-4">
                      <span className="font-bold text-slate-700 text-xs uppercase tracking-wider block">{alert.alert_type}</span>
                      <span className="text-[10px] text-slate-400">{new Date(alert.created_at).toLocaleString()}</span>
                    </td>
                    <td className="p-4 text-slate-600 max-w-xs">{alert.description}</td>
                    <td className="p-4 font-bold text-slate-800">
                      {alert.first_name ? `${alert.first_name} ${alert.last_name}` : 'Unknown/N/A'}
                      <p className="text-[10px] font-normal text-slate-400 mt-0.5">{alert.email || 'N/A'}</p>
                    </td>
                    <td className="p-4 text-slate-500">
                      <p className="font-mono text-xs">IP: {alert.ip_address || 'N/A'}</p>
                      <p className="font-mono text-[10px] text-slate-400 mt-0.5 truncate max-w-xs">Dev: {alert.device_id || 'N/A'}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase ${
                        alert.severity === 'CRITICAL' || alert.severity === 'HIGH' ? 'bg-red-100 text-red-600' :
                        alert.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-600' : 'bg-blue-100 text-blue-600'
                      }`}>
                        {alert.severity}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        alert.is_resolved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {alert.is_resolved ? 'Resolved' : 'Active Warning'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      {!alert.is_resolved && (
                        <button
                          onClick={() => setConfirmModal({ visible: true, id: alert.id })}
                          className="px-3 py-1.5 bg-green-500 hover:bg-green-600 active:scale-95 text-white font-bold rounded-xl text-xs transition"
                        >
                          Resolve Alert
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredAlerts.length === 0 && (
                  <tr><td colSpan="7" className="p-10 text-center text-slate-500">No alerts match your search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal.visible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm p-6 text-center animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Resolve Alert</h3>
            <p className="text-slate-500 text-sm mb-6">Are you sure you want to mark this fraud alert as resolved?</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setConfirmModal({ visible: false, id: null })} className="px-6 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition">Cancel</button>
              <button onClick={handleResolve} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition">Confirm Resolve</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default FraudControl;
